import { motion, AnimatePresence } from 'framer-motion';
import { X, Cpu, BookOpen, ExternalLink, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

interface MachineIAModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CompatibilityGPURow {
  gpuName: string;
  softwareNote: number;
  softwareComment: string;
  llmNote: number;
  llmComment: string;
  performanceNote: number;
  performanceComment: string;
}

interface BudgetScenario {
  budgetMaxCad: number;
  typeLabel: string;
  configComplete: {
    parts: {
      cpu: string;
      gpu: string;
      motherboard: string;
      ram: string;
      storage: string;
      psu: string;
      case: string;
      cooler: string;
    };
    approximatePriceCad: number;
  };
  evolutivityAndLifespan: {
    upgradePath: string;
    lifespan: string;
  };
  nonQuantifiedModels: string;
  compatibilityTable: CompatibilityGPURow[];
  prosCons: { advantages: string[]; disadvantages: string[] };
  eccSection: { title: string; body: string };
  rtx50Option: { model: string; vram: string; note: string };
}

interface LexiconEntry {
  id: string;
  term: string;
  definition: string;
}

interface Reference {
  title: string;
  url: string;
}

const BUDGET_SCENARIOS: BudgetScenario[] = [
  {
    budgetMaxCad: 5000,
    typeLabel: 'PC workstation entrée de gamme',
    configComplete: {
      parts: {
        cpu: 'AMD Ryzen 5 9600X (6 cœurs, Zen 5, socket AM5 65 W)',
        gpu: 'NVIDIA GeForce RTX 5070 Ti 16 Go GDDR7',
        motherboard: 'Carte mère AMD X870E — PCIe 5.0, DDR5, Wi-Fi 7',
        ram: '64 Go DDR5 6000 (2× 32 Go)',
        storage: 'WD Black SN850X 2 To NVMe Gen4',
        psu: 'Corsair RM750e 750 W 80+ Gold, modulaire, ATX 3.1 / PCIe 5.1',
        case: 'Boîtier mid-tower',
        cooler: 'AIO 360 ou tour performante',
      },
      approximatePriceCad: 4200,
    },
    evolutivityAndLifespan: {
      upgradePath: 'Upgrade GPU, ajout 2e carte, second M.2, upgrade CPU Ryzen 7/9 sans changer la carte mère.',
      lifespan: 'Environ 4–5 ans pour l’usage IA avant que la VRAM 16 Go devienne limitante.',
    },
    nonQuantifiedModels: 'Jusqu’à 13B en FP16. 7–24B en Q4 selon les cas.',
    compatibilityTable: [
      { gpuName: 'RTX 5070 Ti 16 Go', softwareNote: 5, softwareComment: 'Compatible Ollama, vLLM, PyTorch, TensorRT.', llmNote: 4, llmComment: '7–24B en Q4. 13B en FP16.', performanceNote: 4, performanceComment: 'Très bon point d’entrée IA.' },
      { gpuName: 'RTX 4060 Ti 16 Go', softwareNote: 5, softwareComment: 'Très bien supportée côté stack IA.', llmNote: 4, llmComment: '7–13B confortables, 32B Q4 possible.', performanceNote: 3, performanceComment: 'Bon débit sur petits modèles.' },
      { gpuName: 'RTX 5070 12 Go', softwareNote: 5, softwareComment: 'Support récent Blackwell.', llmNote: 3, llmComment: '7–13B, plus serré sur gros modèles.', performanceNote: 4, performanceComment: 'Efficace, mais moins de VRAM.' },
    ],
    prosCons: {
      advantages: ['Sous le plafond 5 k CAD', 'Simple à maintenir', 'Très bon pour 7B–24B quantifiés', 'Faible encombrement'],
      disadvantages: ['34B+ hors scope confortable', 'Moins bon pour fine-tuning lourd', '70B hors portée'],
    },
    eccSection: {
      title: 'ECC dans ce budget',
      body: 'Dans ce budget, une config consumer est le meilleur choix. L’ECC n’est pas rentable ici.',
    },
    rtx50Option: { model: 'RTX 5070 Ti 16 Go', vram: '16 Go GDDR7', note: 'Excellent compromis entrée de gamme IA.' },
  },
  {
    budgetMaxCad: 10000,
    typeLabel: 'Workstation milieu de gamme',
    configComplete: {
      parts: {
        cpu: 'AMD Ryzen 7 9700X (8 cœurs, Zen 5, socket AM5 65 W)',
        gpu: 'NVIDIA GeForce RTX 5090 32 Go GDDR7',
        motherboard: 'Carte mère AMD X870E — PCIe 5.0, DDR5, Wi-Fi 7',
        ram: '64 Go DDR5 6000 (2× 32 Go)',
        storage: 'WD Black SN850X 2 To NVMe Gen4',
        psu: '1000 W 80+ Gold modulaire',
        case: 'Boîtier mid-tower',
        cooler: 'AIO 360 ou tour performante',
      },
      approximatePriceCad: 8900,
    },
    evolutivityAndLifespan: {
      upgradePath: 'GPU remplaçable, 2e carte possible, RAM extensible à 128 Go, upgrade Ryzen 9.',
      lifespan: 'Environ 6–7 ans pour l’usage IA avec 32 Go VRAM.',
    },
    nonQuantifiedModels: 'Oui, jusqu’à 34B en FP16. 70B quantifiés confortables.',
    compatibilityTable: [
      { gpuName: 'RTX 5090 32 Go', softwareNote: 5, softwareComment: 'Excellent support IA, Blackwell.', llmNote: 5, llmComment: '70B Q4, 34B FP16 possible.', performanceNote: 5, performanceComment: 'Très haut niveau single-GPU.' },
      { gpuName: 'RTX 4090 24 Go', softwareNote: 5, softwareComment: 'Écosystème mature CUDA / PyTorch / Ollama.', llmNote: 5, llmComment: '34B Q4/Q5 confortable, 70B Q4 possible.', performanceNote: 5, performanceComment: 'Très bon débit inference.' },
      { gpuName: 'RTX 5080 16 Go', softwareNote: 5, softwareComment: 'Support récent et efficace.', llmNote: 4, llmComment: '34B Q4 plus serré.', performanceNote: 5, performanceComment: 'Très bon choix si budget plus serré.' },
    ],
    prosCons: {
      advantages: ['Très fort rapport puissance / flexibilité', '70B quantifiés possibles', 'Excellente base long terme', 'Bonne évolutivité'],
      disadvantages: ['Budget élevé', 'Consommation importante', 'Un seul GPU par défaut'],
    },
    eccSection: {
      title: 'ECC dans ce budget',
      body: 'À 10 k CAD, le meilleur rendement IA reste une config consumer haut de gamme. ECC n’est pas prioritaire ici.',
    },
    rtx50Option: { model: 'RTX 5090 32 Go', vram: '32 Go GDDR7', note: 'Le meilleur choix single-GPU consumer pour IA dans ce budget.' },
  },
  {
    budgetMaxCad: 15000,
    typeLabel: 'Workstation haut de gamme',
    configComplete: {
      parts: {
        cpu: 'AMD Ryzen 9 9950X (16 cœurs / 32 threads, Zen 5)',
        gpu: '2× RTX 5090 32 Go ou 1× RTX 6000 Ada 48 Go',
        motherboard: 'Carte mère AMD X870E avec 2 slots PCIe x16',
        ram: '64–128 Go DDR5 6000',
        storage: 'WD Black SN850X 2 To NVMe + expansion possible',
        psu: '1600 W 80+ Gold/Titanium pour dual GPU',
        case: 'Boîtier full-tower bien ventilé',
        cooler: 'AIO 360 ou refroidissement haut de gamme',
      },
      approximatePriceCad: 14500,
    },
    evolutivityAndLifespan: {
      upgradePath: 'Plateforme prête pour dual GPU, RAM 128 Go, upgrade CPU et stockage additionnel.',
      lifespan: 'Environ 6–7 ans pour des usages IA avancés, modèles 70B+ et gros batchs.',
    },
    nonQuantifiedModels: 'Oui. 2× 5090 ou 6000 Ada permettent des modèles 70B+ dans de meilleures conditions.',
    compatibilityTable: [
      { gpuName: '2× RTX 5090 32 Go', softwareNote: 5, softwareComment: 'Tensor parallelism, gros potentiel VRAM.', llmNote: 5, llmComment: '70B FP16 ou quantifié en réparti.', performanceNote: 5, performanceComment: 'Très haut niveau, mais plus complexe à opérer.' },
      { gpuName: 'RTX 6000 Ada 48 Go', softwareNote: 5, softwareComment: 'Carte pro avec VRAM ECC.', llmNote: 5, llmComment: '70B quantifiés, 34B FP16 confortables.', performanceNote: 5, performanceComment: 'Très stable, usage pro avancé.' },
      { gpuName: 'RTX 5090 32 Go (single)', softwareNote: 5, softwareComment: 'Alternative plus simple.', llmNote: 5, llmComment: 'Très fort single-GPU.', performanceNote: 5, performanceComment: 'Excellente option si on évite le dual GPU.' },
    ],
    prosCons: {
      advantages: ['Très grande VRAM disponible', 'Prêt pour les workloads IA avancés', 'ECC possible via GPU pro', 'Base durable'],
      disadvantages: ['Cher', 'Plus complexe à refroidir / alimenter', 'Dual GPU demande plus de rigueur'],
    },
    eccSection: {
      title: 'ECC dans ce budget',
      body: 'À 15 k CAD, l’ECC devient enfin une option réaliste et pertinente si la stabilité du calcul long est prioritaire.',
    },
    rtx50Option: { model: '2× RTX 5090 ou 1× RTX 6000 Ada', vram: '64 Go ou 48 Go ECC', note: 'Choix entre puissance brute et stabilité pro.' },
  },
];

const LEXICON_ENTRIES: LexiconEntry[] = [
  { id: 'gpu', term: 'GPU', definition: 'Processeur graphique spécialisé dans le calcul parallèle, très utilisé en IA.' },
  { id: 'vram', term: 'VRAM', definition: 'Mémoire du GPU. Plus elle est grande, plus on peut charger de gros modèles.' },
  { id: 'cuda', term: 'CUDA', definition: 'Plateforme NVIDIA utilisée par une grande partie des frameworks IA.' },
  { id: 'inference', term: 'Inference', definition: 'Utilisation d’un modèle déjà entraîné pour produire un résultat.' },
  { id: 'training', term: 'Training / Entraînement', definition: 'Phase d’apprentissage du modèle, plus coûteuse que l’inférence.' },
  { id: 'finetuning', term: 'Fine-tuning', definition: 'Ajustement d’un modèle existant sur un besoin spécifique.' },
  { id: 'quantification', term: 'Quantification', definition: 'Réduction de la précision du modèle pour économiser la mémoire GPU.' },
  { id: 'ecc', term: 'ECC', definition: 'Mémoire avec correction d’erreurs. Utile surtout sur les environnements critiques / pro.' },
  { id: 'x3d', term: 'Ryzen X3D', definition: 'CPU AMD avec cache 3D supplémentaire, souvent utile en jeu et dans certains workloads CPU.' },
];

const REFERENCES: Reference[] = [
  { title: 'Ollama – GPU support', url: 'https://github.com/ollama/ollama/blob/main/docs/gpu.md' },
  { title: 'NVIDIA – Accelerating LLMs with llama.cpp on RTX', url: 'https://developer.nvidia.com/blog/accelerating-llms-with-llama-cpp-on-nvidia-rtx-systems/' },
  { title: 'vLLM – Compatibility matrix', url: 'https://docs.vllm.ai/en/stable/serving/compatibility_matrix.html' },
  { title: 'Puget Systems – Hardware recommendations for AI development', url: 'https://www.pugetsystems.com/solutions/ai/develop/hardware-recommendations/' },
  { title: 'ECC memory – Wikipedia', url: 'https://en.wikipedia.org/wiki/ECC_memory' },
];

function formatCad(value: number): string {
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

function ContenuOngletBudget({ scenario }: { scenario: BudgetScenario }) {
  const { configComplete, evolutivityAndLifespan, compatibilityTable, eccSection } = scenario;

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-4 text-xl font-semibold text-white">Config complète et prix approximatif</h3>
        <div className="rounded-xl border border-purple-500/20 bg-white/5 p-4 text-sm text-gray-300">
          <ul className="space-y-2">
            <li><strong className="text-white">Processeur :</strong> {configComplete.parts.cpu}</li>
            <li><strong className="text-white">GPU :</strong> {configComplete.parts.gpu}</li>
            <li><strong className="text-white">Carte mère :</strong> {configComplete.parts.motherboard}</li>
            <li><strong className="text-white">RAM :</strong> {configComplete.parts.ram}</li>
            <li><strong className="text-white">Stockage :</strong> {configComplete.parts.storage}</li>
            <li><strong className="text-white">Alimentation :</strong> {configComplete.parts.psu}</li>
            <li><strong className="text-white">Boîtier :</strong> {configComplete.parts.case}</li>
            <li><strong className="text-white">Refroidissement :</strong> {configComplete.parts.cooler}</li>
          </ul>
          <p className="mt-4 font-semibold text-white">Prix total estimé : {formatCad(configComplete.approximatePriceCad)} CAD</p>
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-purple-500/20 bg-white/5 p-4">
          <h3 className="mb-3 text-lg font-semibold text-white">Évolutivité</h3>
          <p className="text-sm text-gray-300">{evolutivityAndLifespan.upgradePath}</p>
        </div>
        <div className="rounded-xl border border-purple-500/20 bg-white/5 p-4">
          <h3 className="mb-3 text-lg font-semibold text-white">Durée de vie</h3>
          <p className="text-sm text-gray-300">{evolutivityAndLifespan.lifespan}</p>
        </div>
      </section>

      <section>
        <h3 className="mb-4 text-xl font-semibold text-white">Modèles non quantifiés</h3>
        <p className="text-sm text-gray-300">{scenario.nonQuantifiedModels}</p>
      </section>

      <section>
        <h3 className="mb-4 text-xl font-semibold text-white">Compatibilité des cartes</h3>
        <div className="space-y-4">
          {compatibilityTable.map((row) => (
            <div key={row.gpuName} className="rounded-xl border border-purple-500/20 bg-white/5 p-4">
              <h4 className="text-lg font-semibold text-white mb-2">{row.gpuName}</h4>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-300">
                <div>
                  <p className="font-semibold text-white">Logiciels {row.softwareNote}/5</p>
                  <p>{row.softwareComment}</p>
                </div>
                <div>
                  <p className="font-semibold text-white">LLM {row.llmNote}/5</p>
                  <p>{row.llmComment}</p>
                </div>
                <div>
                  <p className="font-semibold text-white">Performance {row.performanceNote}/5</p>
                  <p>{row.performanceComment}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4">
          <h3 className="mb-3 text-lg font-semibold text-white">Avantages</h3>
          <ul className="space-y-2 text-sm text-gray-300 list-disc pl-5">
            {scenario.prosCons.advantages.map((adv) => <li key={adv}>{adv}</li>)}
          </ul>
        </div>
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4">
          <h3 className="mb-3 text-lg font-semibold text-white">Inconvénients</h3>
          <ul className="space-y-2 text-sm text-gray-300 list-disc pl-5">
            {scenario.prosCons.disadvantages.map((dis) => <li key={dis}>{dis}</li>)}
          </ul>
        </div>
      </section>

      <section className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-4">
        <h3 className="mb-3 text-lg font-semibold text-white flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-indigo-400" /> ECC</h3>
        <p className="text-sm text-gray-300"><strong className="text-white">{eccSection.title} :</strong> {eccSection.body}</p>
      </section>

      <section className="rounded-xl border border-pink-500/20 bg-pink-500/5 p-4">
        <p className="text-sm text-gray-300">
          <strong className="text-white">Équivalent série RTX 50 :</strong> {scenario.rtx50Option.model} ({scenario.rtx50Option.vram}). {scenario.rtx50Option.note}
        </p>
      </section>
    </div>
  );
}

export default function MachineIAModal({ isOpen, onClose }: MachineIAModalProps) {
  const [activeTab, setActiveTab] = useState(0);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-2xl max-w-6xl w-full my-8 border border-purple-500/30 shadow-2xl shadow-purple-500/20"
          >
            <div className="relative p-8 border-b border-purple-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                    <Cpu className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                      Machine IA
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Scénarios d’achat pour une machine dédiée à l’IA
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              <div className="bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-purple-600/20 rounded-xl p-8 border-2 border-purple-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">Résumé exécutif</h3>
                <p className="text-gray-300 text-center text-lg">
                  Comparatif de 3 budgets (5 000, 10 000 et 15 000 CAD) pour une machine dédiée à l’inférence et à l’entraînement léger de modèles d’IA. Le but est d’orienter les choix matériels selon la VRAM, la compatibilité logicielle, la capacité LLM, l’évolutivité et la rentabilité.
                </p>
              </div>

              <div>
                <h3 className="mb-4 text-2xl font-semibold text-white">Les 3 budgets</h3>
                <div className="mb-6 flex gap-2 border-b border-purple-500/20">
                  {BUDGET_SCENARIOS.map((scenario, idx) => (
                    <button
                      key={scenario.budgetMaxCad}
                      type="button"
                      onClick={() => setActiveTab(idx)}
                      className={`rounded-t-md border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                        activeTab === idx
                          ? 'border-pink-400 text-pink-300'
                          : 'border-transparent text-gray-400 hover:text-white'
                      }`}
                    >
                      {scenario.budgetMaxCad === 5000 ? '5 k CAD' : scenario.budgetMaxCad === 10000 ? '10 k CAD' : '15 k CAD'}
                    </button>
                  ))}
                </div>
                <ContenuOngletBudget scenario={BUDGET_SCENARIOS[activeTab]} />
              </div>

              <section>
                <h3 className="mb-4 text-2xl font-semibold text-white flex items-center gap-2"><BookOpen className="w-6 h-6 text-purple-400" /> Lexique</h3>
                <div className="space-y-3">
                  {LEXICON_ENTRIES.map((entry) => (
                    <details key={entry.id} className="rounded-xl border border-purple-500/20 bg-white/5 p-4">
                      <summary className="cursor-pointer font-semibold text-white">{entry.term}</summary>
                      <p className="mt-2 text-sm text-gray-300">{entry.definition}</p>
                    </details>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-4 text-2xl font-semibold text-white flex items-center gap-2"><ExternalLink className="w-6 h-6 text-purple-400" /> Références</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  {REFERENCES.map((ref) => (
                    <li key={ref.url}>
                      <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                        {ref.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            <div className="p-6 border-t border-purple-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 font-semibold shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
